/**
 * modal.js — Global modal controller
 *
 * Loaded via <script type="module" defer> in layout/theme.liquid.
 * Handles all <dialog> elements driven by data-modal-open / data-modal-close.
 *
 * API surface:
 *   data-modal-open="<dialogId>"  on any clickable element → opens matching dialog
 *   data-modal-close              on any element inside a dialog → closes ancestor dialog
 *
 * Accessibility:
 *   - Moves focus into first focusable element on open.
 *   - Returns focus to the triggering element on close.
 *   - ESC is handled natively by <dialog>.
 *   - Scroll lock via html { overflow: hidden } while any dialog is open.
 */

/**
 * Returns the first focusable element inside a container.
 * @param {HTMLElement} container
 * @returns {HTMLElement|null}
 */
function firstFocusable(container) {
  return container.querySelector(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
}

/**
 * Applies scroll lock when at least one dialog is open.
 * Restores scroll when none are open.
 */
function syncScrollLock() {
  const hasOpen = document.querySelector('dialog[open]') !== null;
  document.documentElement.style.overflow = hasOpen ? 'hidden' : '';
}

/**
 * Opens a <dialog> by id, moves focus into it, and records the trigger.
 * @param {string} dialogId
 * @param {HTMLElement} trigger
 */
function openModal(dialogId, trigger) {
  const dialog = document.getElementById(dialogId);
  if (!dialog || !(dialog instanceof HTMLDialogElement)) return;

  // Store trigger reference for focus-return on close
  dialog._modalTrigger = trigger;

  dialog.showModal();
  syncScrollLock();

  // Move focus into dialog — prefer close button, then first focusable
  const closeBtn = dialog.querySelector('[data-modal-close]');
  const target = closeBtn || firstFocusable(dialog);
  if (target) target.focus();
}

/**
 * Closes a <dialog> and returns focus to the element that opened it.
 * @param {HTMLDialogElement} dialog
 */
function closeModal(dialog) {
  if (!dialog || !dialog.open) return;
  dialog.close();
  syncScrollLock();

  // Return focus to trigger
  if (dialog._modalTrigger) {
    dialog._modalTrigger.focus();
    dialog._modalTrigger = null;
  }
}

// ----------------------------------------------------------------
// Event delegation — one listener per event type for the whole doc
// ----------------------------------------------------------------

/** Click: open trigger or close button */
document.addEventListener('click', (e) => {
  // Open trigger
  const openTrigger = e.target.closest('[data-modal-open]');
  if (openTrigger) {
    const id = openTrigger.dataset.modalOpen;
    if (id) openModal(id, openTrigger);
    return;
  }

  // Close button inside dialog
  const closeTrigger = e.target.closest('[data-modal-close]');
  if (closeTrigger) {
    const dialog = closeTrigger.closest('dialog');
    if (dialog) closeModal(dialog);
    return;
  }

  // Backdrop click: target IS the <dialog> element (not inner content)
  if (e.target instanceof HTMLDialogElement && e.target.open) {
    closeModal(e.target);
  }
});

/**
 * 'close' event fires after dialog.close() completes (including native ESC).
 * Use capture phase so we catch it before it bubbles away.
 * We do NOT call closeModal here (infinite loop risk) — just restore scroll.
 */
document.addEventListener(
  'close',
  (e) => {
    if (!(e.target instanceof HTMLDialogElement)) return;
    // Return focus to stored trigger (handles ESC case — JS close handler may not have run)
    if (e.target._modalTrigger) {
      e.target._modalTrigger.focus();
      e.target._modalTrigger = null;
    }
    syncScrollLock();
  },
  true /* capture */
);
